import { useCallback, useMemo } from "react";
import { AdminsRepository } from "@/api/admins/repository";
import { AdminsService } from "@/api/admins/service";
import { ClientsRepository } from "@/api/clients/repository";
import { ClientsService } from "@/api/clients/service";
import type { IGetClientsRequest } from "@/api/clients/types";
import { ContractorsRepository } from "@/api/contractors/repository";
import { ContractorsService } from "@/api/contractors/service";
import { InspectorsRepository } from "@/api/inspectors/repository";
import { InspectorsService } from "@/api/inspectors/service";
import { LoanOfficersRepository } from "@/api/loan-officers/repository";
import { LoanOfficersService } from "@/api/loan-officers/service";
import type { IProjectMember } from "@/pages/Projects/types";
import type { IUser } from "@/types/users";
import { UserRole } from "@/types/users";
import type { ISelectOption } from "@/ui-kit";
import { Avatar } from "@/ui-kit";
import type { IFetchOptions } from "../useSearchOptions";
import { useSearchOptions } from "../useSearchOptions";

/** Roles that have a user list endpoint. */
export type IUserControlRole =
  | UserRole.Admin
  | UserRole.Client
  | UserRole.Contractor
  | UserRole.Inspector
  | UserRole.LoanOfficer;

// All role endpoints take the same list params. `hasCompany` is missing from
// the shared request types, but the picker has always sent it.
type IGetUsersRequest = IGetClientsRequest & { hasCompany: string };

const PAGE_SIZE = "20";

// useSearchOptions aborts its own requests, so repositories run without `abort`.
const createRepository = (role: IUserControlRole) => {
  switch (role) {
    case UserRole.Admin:
      return new AdminsRepository(new AdminsService());
    case UserRole.Client:
      return new ClientsRepository(new ClientsService());
    case UserRole.Contractor:
      return new ContractorsRepository(new ContractorsService());
    case UserRole.Inspector:
      return new InspectorsRepository(new InspectorsService());
    case UserRole.LoanOfficer:
      return new LoanOfficersRepository(new LoanOfficersService());
  }
};

const toOption = (user: IUser, role: IUserControlRole): ISelectOption => {
  const member: IProjectMember = {
    id: user.id.toString(),
    fullName: user.fullName,
    email: user.email,
    role,
  };

  return {
    id: member.id,
    text: member.fullName,
    icon: <Avatar src={user.profilePicturePath} showDetails={false} />,
    data: member,
  };
};

/** Paginated, searchable list of users of one role. */
export const useUserOptions = (role: IUserControlRole) => {
  const repository = useMemo(() => createRepository(role), [role]);

  const fetchOptions = useCallback<IFetchOptions>(
    (query, page, signal) => {
      const params: IGetUsersRequest = {
        searchQuery: query,
        page: `${page}`,
        size: PAGE_SIZE,
        sortField: "fullName",
        sortOrder: "asc",
        hasCompany: "true",
      };

      return repository.getAll(params, { signal }).then((response) => ({
        options: response.content.map((user) => toOption(user, role)),
        hasMore: response.page < response.totalPages,
      }));
    },
    [repository, role],
  );

  return useSearchOptions(fetchOptions);
};
